"use client"

import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Button } from "@/components/ui/button"
import {
  Field,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { toast } from "sonner"
import axios from "axios"
import { useSpamGuard } from "@/components/spam/use-spam-guard"

const complaintFormSchema = z
  .object({
    isAnonymous: z.enum(["true", "false"], {
      message: "Por favor indica si quieres llevar a cabo la denuncia de forma anónima",
    }),
    name: z.string().optional(),
    profile: z.enum(["cliente", "proveedor", "trabajador"], {
      message: "Por favor selecciona tu perfil",
    }),
    description: z
      .string()
      .min(10, "Por favor describe la denuncia con al menos 10 caracteres"),
  })
  .refine(
    (data) => data.isAnonymous === "true" || (data.name?.trim().length ?? 0) > 0,
    {
      path: ["name"],
      message: "Por favor ingresa tu nombre",
    }
  )

type ComplaintFormData = z.infer<typeof complaintFormSchema>

interface ComplaintFormProps {
  title?: string
  description?: string
}

export function ComplaintForm({
  title = "Canal de Denuncias",
  description = "Si quieres comunicar una situación irregular, puedes hacerlo a través de este formulario. Todo lo que digas es absolutamente confidencial y, si lo prefieres, puedes hacerlo de forma anónima.",
}: ComplaintFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const spam = useSpamGuard()

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    reset,
  } = useForm<ComplaintFormData>({
    resolver: zodResolver(complaintFormSchema),
    mode: "onChange",
    defaultValues: {
      isAnonymous: undefined,
      name: "",
      profile: undefined,
      description: "",
    },
  })

  const isAnonymousValue = watch("isAnonymous")
  const showNameField = isAnonymousValue === "false"

  const onSubmit = async (data: ComplaintFormData) => {
    if (!spam.ready) {
      toast.error('Por favor completa la verificación de seguridad antes de enviar.')
      return
    }
    setIsSubmitting(true)
    try {
      const isAnonymous = data.isAnonymous === "true"
      await axios.post("/api/send-complaint-email", {
        isAnonymous,
        name: isAnonymous ? null : data.name?.trim() ?? null,
        profile: data.profile,
        description: data.description.trim(),
        ...spam.getSpamPayload(),
      })

      toast.success("Denuncia enviada. Gracias por confiar en nosotros.")
      reset()
      spam.reset()
    } catch (error) {
      console.error("Error sending complaint:", error)
      toast.error("Error al enviar la denuncia. Por favor, inténtalo de nuevo.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section className="py-32" style={{ backgroundColor: "#FFFFFF" }} id="canal-de-denuncias">
      <div className="container mx-auto max-w-4xl px-6">
        <div className="flex flex-col gap-4 text-left mb-12">
          <h2 className="text-3xl lg:text-2xl">{title}</h2>
          <p className="text-muted-foreground max-w-2xl">{description}</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {spam.fields}
          <FieldGroup className="space-y-6">
            <Field>
              <FieldLabel className="text-sm text-gray-800">
                ¿Quieres llevar a cabo la denuncia de forma anónima?
              </FieldLabel>
              <p className="text-xs text-gray-500 mb-2">
                No es necesario que digas tu nombre si no lo consideras.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="true"
                    {...register("isAnonymous")}
                    className="w-4 h-4 border-gray-300 focus:ring-[#0A3043]"
                    style={{ accentColor: "#0A3043" }}
                  />
                  <span className="text-sm text-gray-900">Prefiero no aportar mi nombre</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="false"
                    {...register("isAnonymous")}
                    className="w-4 h-4 border-gray-300 focus:ring-[#0A3043]"
                    style={{ accentColor: "#0A3043" }}
                  />
                  <span className="text-sm text-gray-900">Prefiero dar mi nombre</span>
                </label>
              </div>
              {errors.isAnonymous && (
                <p className="text-xs text-red-500 mt-1">{errors.isAnonymous.message}</p>
              )}
            </Field>

            {showNameField && (
              <Field>
                <FieldLabel htmlFor="name" className="text-sm text-gray-800">
                  Pon tu nombre a continuación
                </FieldLabel>
                <Input
                  id="name"
                  type="text"
                  placeholder="Juan Pérez"
                  {...register("name")}
                  className="text-sm text-gray-900 placeholder:text-gray-500 border border-gray-200 focus-visible:ring-[#1A3043]/30 bg-white"
                />
                {errors.name && (
                  <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>
                )}
              </Field>
            )}

            <Field>
              <FieldLabel className="text-sm text-gray-800">Perfil del denunciante</FieldLabel>
              <div className="flex flex-col sm:flex-row gap-3 mt-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="cliente"
                    {...register("profile")}
                    className="w-4 h-4 border-gray-300 focus:ring-[#0A3043]"
                    style={{ accentColor: "#0A3043" }}
                  />
                  <span className="text-sm text-gray-900">Cliente</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="proveedor"
                    {...register("profile")}
                    className="w-4 h-4 border-gray-300 focus:ring-[#0A3043]"
                    style={{ accentColor: "#0A3043" }}
                  />
                  <span className="text-sm text-gray-900">Proveedor</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="trabajador"
                    {...register("profile")}
                    className="w-4 h-4 border-gray-300 focus:ring-[#0A3043]"
                    style={{ accentColor: "#0A3043" }}
                  />
                  <span className="text-sm text-gray-900">Trabajador/a</span>
                </label>
              </div>
              {errors.profile && (
                <p className="text-xs text-red-500 mt-1">{errors.profile.message}</p>
              )}
            </Field>

            <Field>
              <FieldLabel htmlFor="description" className="text-sm text-gray-800">
                Puedes escribir a continuación tu denuncia
              </FieldLabel>
              <p className="text-xs text-gray-500 mb-2">
                Todo lo que digas es absolutamente confidencial.
              </p>
              <Textarea
                id="description"
                placeholder="Escribe aquí tu denuncia..."
                rows={6}
                {...register("description")}
                className="text-sm text-gray-900 placeholder:text-gray-500 border border-gray-200 focus-visible:ring-[#1A3043]/30 resize-none bg-white"
              />
              {errors.description && (
                <p className="text-xs text-red-500 mt-1">{errors.description.message}</p>
              )}
            </Field>

            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={!isValid || isSubmitting || !spam.ready}
                className="bg-[#1A3043] hover:bg-[#0A3043] text-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Enviando..." : "Enviar"}
              </Button>
            </div>
          </FieldGroup>
        </form>
      </div>
    </section>
  )
}
